import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { Box, Typography } from '@mui/material';
import { GoogleMapsContext } from '../../contexts/GoogleMapsContext';

const defaultContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
  overflow: 'hidden',
};

const defaultCenter = {
  lat: 47.4979, // Budapest
  lng: 19.0402,
};

const defaultOptions = {
  disableDefaultUI: false, // Engedélyezi az alapértelmezett UI vezérlőket (zoom, pan)
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
};

const MapComponent = ({
  center = defaultCenter,
  zoom = 12,
  markers = [],
  onMapClick = null,
  onMarkerClick = null,
  mapBounds = null,
  highlightMarkerId = null,
  containerStyle = null,
  options = null, // a defaultOptions felülírása (pl. gestureHandling)
}) => {
  const navigate = useNavigate();
  const { isLoaded } = useContext(GoogleMapsContext);
  const effectiveContainerStyle = containerStyle || defaultContainerStyle;
  const [map, setMap] = useState(null);
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);
  const [hoverInfoWindow, setHoverInfoWindow] = useState(null);

  const onLoad = useCallback(function callback(mapInstance) {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  const handleMarkerClick = (markerId) => {
    setActiveInfoWindow(markerId); // Beállítja az aktív infó ablakot
    if (onMarkerClick) {
      onMarkerClick(markerId);
    }
  };

  const handleMapClick = (event) => {
    if (onMapClick) {
      onMapClick({
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });
    }
    setActiveInfoWindow(null); // Bezárja az esetlegesen nyitott infó ablakokat
  };

  // ÚJ: Térkép nézet és infó ablak kezelése a propok alapján
  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;

    if (mapBounds) {
      // Ha mapBounds van megadva, igazítsa a térképet a határokhoz
      const bounds = new window.google.maps.LatLngBounds(
        { lat: mapBounds.south, lng: mapBounds.west },
        { lat: mapBounds.north, lng: mapBounds.east }
      );
      map.fitBounds(bounds);
      setActiveInfoWindow(null); // Bezárja az infó ablakot, ha bounds-ra váltunk
    } else if (highlightMarkerId) {
      // Ha egy adott markert ki kell emelni, arra ugrik és megnyitja az infó ablakát
      const markerToHighlight = markers.find(m => m.id === highlightMarkerId);
      if (markerToHighlight) {
        map.panTo({ lat: markerToHighlight.lat, lng: markerToHighlight.lng });
        map.setZoom(15); // Közelebbi zoom egy adott markerre
        setActiveInfoWindow(highlightMarkerId);
      }
    } else {
      // Alapértelmezett viselkedés, ha nincs bounds és nincs kiemelés
      map.setCenter(center);
      map.setZoom(zoom);
      setActiveInfoWindow(null);
    }
  }, [map, mapBounds, highlightMarkerId, markers, center, zoom]);


  const resolveIcon = (icon) => {
    if (!icon || !window.google?.maps?.Size) return undefined;
    if (icon.scaledSize && !(icon.scaledSize instanceof window.google.maps.Size)) {
      return {
        ...icon,
        scaledSize: new window.google.maps.Size(icon.scaledSize.width, icon.scaledSize.height),
      };
    }
    return icon;
  };

  if (!isLoaded) return <Box sx={effectiveContainerStyle} />;

  return (
    <GoogleMap
      mapContainerStyle={effectiveContainerStyle}
      // A center és zoom propokat most már a fenti useEffect kezeli,
      // de az alapértelmezett értékeket továbbra is átadhatjuk, ha a useEffect nem fut le azonnal
      center={center}
      zoom={zoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={handleMapClick}
      options={options ? { ...defaultOptions, ...options } : defaultOptions}
    >
      {markers.map((marker) => (
        <MarkerF
          key={marker.id}
          position={{ lat: marker.lat, lng: marker.lng }}
          title={marker.title}
          icon={resolveIcon(marker.icon)}
          onClick={() => handleMarkerClick(marker.id)}
          onMouseOver={() => setHoverInfoWindow(marker.id)}
          onMouseOut={() => setHoverInfoWindow(null)}
        >
          {hoverInfoWindow === marker.id && activeInfoWindow !== marker.id && (
            <InfoWindowF
              onCloseClick={() => setHoverInfoWindow(null)}
              options={{ disableAutoPan: true }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5, maxWidth: 200 }}>
                {marker.avatarUrl ? (
                  <img
                    src={marker.avatarUrl}
                    alt=""
                    style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {marker.categoryIcon && (marker.categoryIcon.includes('/') || marker.categoryIcon.includes('.'))
                      ? <img src={marker.categoryIcon} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                      : (marker.categoryIcon || '📍')}
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2 }}>
                    {marker.title}
                  </Typography>
                  {marker.categoryName && (
                    <Typography variant="caption" color="text.secondary">
                      {marker.categoryName}
                    </Typography>
                  )}
                </Box>
              </Box>
            </InfoWindowF>
          )}
          {activeInfoWindow === marker.id && (
            <InfoWindowF
              onCloseClick={() => setActiveInfoWindow(null)}
              options={{ headerDisabled: true, maxWidth: 220 }}
            >
              <Box sx={{ p: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5, gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
                    {marker.title}
                  </Typography>
                  <Box
                    component="button"
                    onClick={() => setActiveInfoWindow(null)}
                    sx={{ cursor: 'pointer', border: 'none', background: 'none', p: 0, color: 'text.secondary', fontSize: 18, lineHeight: 1, flexShrink: 0 }}
                  >
                    ×
                  </Box>
                </Box>
                {marker.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    {marker.description}
                  </Typography>
                )}
                {marker.link && (
                  <Box
                    component="button"
                    onClick={() => navigate(marker.link)}
                    sx={{
                      cursor: 'pointer', border: 'none', background: 'none', p: 0,
                      color: 'primary.main', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'underline',
                    }}
                  >
                    Megtekintés →
                  </Box>
                )}
              </Box>
            </InfoWindowF>
          )}
        </MarkerF>
      ))}
    </GoogleMap>
  );
};

export default React.memo(MapComponent);